<template>
  <div class="tl">
    <Breadcrumbs main="Фейковые команды" title="Список фейковых команд"/>

    <Modal name="DeleteFakeTeam" title="Удаление фейковой команды" v-model="isShowDeleteFakeTeamModal"
           :params="{ id: selectedFakeTeamId }"/>
    <Modal name="EditFakeTeam" title="Редактирование фейковой команды" v-model="isShowEditFakeTeamModal"
           :params="{ id: selectedFakeTeamId }"/>
    <Modal name="CreateFakeTeam" title="Создание фейковой команды" v-model="isShowCreateFakeTeamModal"/>

    <div class="container-fluid tl-container">

      <!-- ================== TOOLBAR ================== -->
      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingFakeTeams }">
            <i class="fa fa-search tl-search__icon"></i>
            <input
                ref="search"
                type="search"
                class="tl-search__input"
                v-model="searchModel"
                placeholder="Поиск по ID или названию фейк-команды…"
                @keydown.esc.prevent="clearSearch"
            />
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <div class="tl-spacer"></div>

          <div class="tl-seg" role="group" aria-label="Вид">
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'table' }"
                    @click="setViewMode('table')" v-b-tooltip.hover.bottom="'Таблица'">
              <i class="fa fa-list-ul"></i>
            </button>
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'grid' }"
                    @click="setViewMode('grid')" v-b-tooltip.hover.bottom="'Карточки'">
              <i class="fa fa-th-large"></i>
            </button>
          </div>

          <button type="button" class="tl-icon-btn" :class="{ 'is-on': density === 'compact' }"
                  @click="toggleDensity" v-b-tooltip.hover.bottom="'Плотность строк'">
            <i class="fa fa-bars"></i>
          </button>

          <router-link :to="{ name: 'teams' }" class="tl-icon-btn"
                       v-b-tooltip.hover.bottom="'Реальные команды'">
            <i class="fa fa-users"></i>
          </router-link>

          <button type="button" class="tl-icon-btn" :class="{ 'is-spin': isLoadingFakeTeams }"
                  @click="refresh" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>

          <button type="button" class="tl-btn-primary" @click="createFakeTeam">
            <i class="fa fa-plus"></i> Создать команду
          </button>
        </div>

        <div v-if="searchApplied" class="tl-chips">
          <span class="tl-chips__label">Фильтры:</span>
          <button type="button" class="tl-chip" @click="clearSearch">
            <i class="fa fa-search"></i> «{{ searchApplied }}» <i class="fa fa-times tl-chip__x"></i>
          </button>
          <button type="button" class="tl-chips__reset" @click="clearSearch">Сбросить всё</button>
        </div>
      </div>

      <!-- ================== CONTENT ================== -->
      <div class="tl-panel" :class="['tl-panel--' + density]">

        <div v-if="isLoadingFakeTeams" class="tl-skeleton">
          <div v-for="n in 6" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--avatar"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-user-secret"></i></div>
          <div class="tl-empty__title">
            {{ searchApplied ? 'Ничего не нашлось' : 'Фейковых команд пока нет' }}
          </div>
          <div class="tl-empty__text">
            {{ searchApplied
              ? 'Попробуйте изменить условия поиска.'
              : 'Фейковые команды нужны, чтобы добить сетку до нужного количества слотов.' }}
          </div>
          <div class="tl-empty__actions">
            <button v-if="searchApplied" type="button" class="tl-btn-ghost-2" @click="clearSearch">
              Сбросить поиск
            </button>
            <button type="button" class="tl-btn-primary" @click="createFakeTeam">
              <i class="fa fa-plus"></i> Создать команду
            </button>
          </div>
        </div>

        <!-- table -->
        <div v-else-if="viewMode === 'table'" class="tl-table-wrap">
          <b-table
              ref="table"
              class="tl-table"
              responsive
              hover
              borderless
              show-empty
              :items="rows"
              :fields="fields"
              @row-clicked="editFakeTeam"
          >
            <template #cell(id)="{ item }">
              <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                      v-b-tooltip.hover.right="'Скопировать ID'">
                <span>#{{ item.id }}</span>
                <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(name)="{ item }">
              <div class="tl-name">
                <span class="tl-name__logo">{{ initials(item.name) }}</span>
                <button type="button" class="tl-name__title" @click.stop="editFakeTeam(item)" :title="item.name">
                  {{ item.name }}
                </button>
              </div>
            </template>

            <template #cell(game)="{ item }">
              <span class="tl-muted-strong">{{ item.x_game || '—' }}</span>
            </template>

            <template #cell(members)="{ item }">
              <div v-if="item.x_nicks.length" class="tl-nicks">
                <span v-for="(nick, index) in item.x_nicks" :key="item.id + '-n' + index" class="tl-nick">
                  {{ nick }}
                </span>
              </div>
              <span v-else class="tl-muted">состав не заполнен</span>
            </template>

            <template #cell(size)="{ item }">
              <span class="tl-pill">{{ item.x_memberCount }}</span>
            </template>

            <template #cell(action)="{ item }">
              <div class="tl-actions" @click.stop>
                <button type="button" class="tl-act" @click="editFakeTeam(item)"
                        v-b-tooltip.hover.top="'Редактировать'">
                  <i class="fa fa-pencil"></i>
                </button>
                <button type="button" class="tl-act tl-act--danger" @click="deleteFakeTeam(item.id)"
                        v-b-tooltip.hover.top="'Удалить'">
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
            </template>

            <template #empty>
              <div class="tl-empty tl-empty--inline">Ничего не найдено</div>
            </template>
          </b-table>
        </div>

        <!-- grid -->
        <div v-else class="tl-grid">
          <article v-for="item in rows" :key="'card' + item.id" class="tl-card" @click="editFakeTeam(item)">
            <header class="tl-card__head">
              <span class="tl-card__logo">{{ initials(item.name) }}</span>
              <div class="tl-card__headtext">
                <button type="button" class="tl-card__title" @click.stop="editFakeTeam(item)" :title="item.name">
                  {{ item.name }}
                </button>
                <div class="tl-card__sub">
                  <span class="tl-id tl-id--sm">#{{ item.id }}</span>
                  <span class="tl-dot-sep">·</span>
                  <span>{{ item.x_game || '—' }}</span>
                </div>
              </div>
            </header>

            <div class="tl-card__rows">
              <div class="tl-card__row">
                <span><i class="fa fa-users tl-muted"></i> В составе</span>
                <span class="tl-muted-strong">{{ item.x_memberCount }}</span>
              </div>
              <div v-if="item.x_nicks.length" class="tl-nicks">
                <span v-for="(nick, index) in item.x_nicks" :key="item.id + '-cn' + index" class="tl-nick">
                  {{ nick }}
                </span>
              </div>
              <div v-else class="tl-muted">Состав не заполнен</div>
            </div>

            <footer class="tl-card__foot" @click.stop>
              <button type="button" class="tl-act" @click="editFakeTeam(item)" v-b-tooltip.hover.top="'Редактировать'">
                <i class="fa fa-pencil"></i>
              </button>
              <span class="tl-spacer"></span>
              <button type="button" class="tl-act tl-act--danger" @click="deleteFakeTeam(item.id)"
                      v-b-tooltip.hover.top="'Удалить'">
                <i class="fa fa-trash-o"></i>
              </button>
            </footer>
          </article>
        </div>

        <div v-if="!isLoadingFakeTeams && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ totalPagesOfFakeTeams || 0 }}</b>
          </div>
          <b-pagination
              class="tl-pagination my-0"
              v-model="fakeTeamsCurrentPage"
              :total-rows="totalPagesOfFakeTeams"
              :per-page="perPage"
              :limit="7"
              @change="goToPage"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import Modal from '@/components/modals/index.vue'
import {mapState} from "vuex";

const LS = {
  view: "fakeTeamsListView",
  density: "fakeTeamsListDensity",
};

export default {
  name: "FakeTeams",
  components: {Modal},
  mixins: [PaginationHelperMixin],
  data() {
    return {
      fakeTeamsPageKey: 'pageNumberFakeTeams',
      perPage: 10,

      searchModel: '',
      searchApplied: '',
      searchTimer: null,

      viewMode: 'table',
      density: 'cozy',

      fields: [
        {key: 'id', label: 'ID'},
        {key: 'name', label: 'Название', sortable: true},
        {key: 'game', label: 'Дисциплина', sortable: true},
        {key: 'size', label: 'В составе', sortable: true},
        {key: 'members', label: 'Ники игроков'},
        {key: 'action', label: '', thClass: 'tl-th--act', tdClass: 'tl-td--act'},
      ],

      isLoadingFakeTeams: false,
      isShowDeleteFakeTeamModal: false,
      isShowEditFakeTeamModal: false,
      isShowCreateFakeTeamModal: false,
      selectedFakeTeamId: 0,
      fakeTeamsCurrentPage: 1,
    }
  },
  computed: {
    ...mapState('moduleFakeTeams', ['fakeTeams', 'totalPagesOfFakeTeams']),

    /** Ники игроков вытаскиваем один раз здесь, чтобы шаблон не считал их на каждый рендер. */
    rows() {
      return (this.fakeTeams || []).map(team => {
        const members = team.members || [];
        return {
          ...team,
          x_game: (team.game && team.game.name) || '',
          x_memberCount: members.length,
          x_nicks: members
              .map(member => this.getNickOfFakeUser(member, team.game))
              .filter(Boolean),
        };
      });
    },

    rangeFrom() {
      if (!this.totalPagesOfFakeTeams) return 0;
      return (this.fakeTeamsCurrentPage - 1) * this.perPage + 1;
    },

    rangeTo() {
      return Math.min(this.fakeTeamsCurrentPage * this.perPage, this.totalPagesOfFakeTeams || 0);
    },
  },
  watch: {
    searchModel(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.searchApplied === value.trim()) return;
        this.searchApplied = value.trim();
        this.fakeTeamsCurrentPage = 1;
        this.fetchFakeTeams();
      }, 400);
    },

    // после закрытия модалки состав/название могли измениться — перечитываем страницу
    isShowCreateFakeTeamModal(isOpen, wasOpen) {
      if (wasOpen && !isOpen) this.fetchFakeTeams();
    },
    isShowEditFakeTeamModal(isOpen, wasOpen) {
      if (wasOpen && !isOpen) this.fetchFakeTeams();
    },
    isShowDeleteFakeTeamModal(isOpen, wasOpen) {
      if (wasOpen && !isOpen) this.fetchFakeTeams();
    },
  },
  mounted() {
    this.restorePreferences();
    this.fakeTeamsCurrentPage = Number(this.getCurrentPageNumber(this.fakeTeamsPageKey)) || 1;
    this.fetchFakeTeams();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchFakeTeams() {
      this.isLoadingFakeTeams = true;
      this.recordPageToStorage(this.fakeTeamsCurrentPage, this.fakeTeamsPageKey);
      try {
        await this.$store.dispatch('moduleFakeTeams/getFakeTeams', {
          page: this.fakeTeamsCurrentPage,
          ...(this.searchApplied && {search: this.searchApplied}),
        });
      } catch (e) {
        this.$toasted.error("Не удалось загрузить фейковые команды", {position: 'top-right', duration: 3000});
      } finally {
        this.isLoadingFakeTeams = false;
      }
    },

    refresh() {
      this.fetchFakeTeams();
    },

    goToPage(pageNumber) {
      this.fakeTeamsCurrentPage = pageNumber;
      this.$nextTick(this.fetchFakeTeams);
    },

    clearSearch() {
      this.searchModel = '';
      if (!this.searchApplied) return;
      this.searchApplied = '';
      this.fakeTeamsCurrentPage = 1;
      this.fetchFakeTeams();
    },

    deleteFakeTeam(id) {
      this.selectedFakeTeamId = id;
      this.isShowDeleteFakeTeamModal = true;
    },

    editFakeTeam(item) {
      this.selectedFakeTeamId = typeof item === 'object' ? item.id : item;
      this.isShowEditFakeTeamModal = true;
    },

    createFakeTeam() {
      this.isShowCreateFakeTeamModal = true;
    },

    getNickOfFakeUser(user, game) {
      if (!user || !game || !user.game_profiles || !user.game_profiles.length) return '';
      const profile = user.game_profiles.find(item => item.game_id === game.id);
      return (profile && profile.fields && profile.fields.nick) || '';
    },

    copyId(id) {
      const done = () => this.$toasted.success(`ID ${id} скопирован`, {position: 'top-right', duration: 1500});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(done).catch(() => {
        });
        return;
      }
      const area = document.createElement("textarea");
      area.value = String(id);
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
        done();
      } finally {
        document.body.removeChild(area);
      }
    },

    initials(name) {
      if (!name) return "?";
      return String(name).trim().slice(0, 2).toUpperCase();
    },

    restorePreferences() {
      try {
        const view = localStorage.getItem(LS.view);
        if (view === "grid" || view === "table") this.viewMode = view;

        const density = localStorage.getItem(LS.density);
        if (density === "compact" || density === "cozy") this.density = density;
      } catch (e) { /* повреждённые настройки — берём значения по умолчанию */
      }
    },

    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem(LS.view, mode);
    },

    toggleDensity() {
      this.density = this.density === "compact" ? "cozy" : "compact";
      localStorage.setItem(LS.density, this.density);
    },

    onKeydown(event) {
      const tag = (event.target && event.target.tagName) || "";
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag) || (event.target && event.target.isContentEditable);

      if (event.key === "Escape") {
        if (isTyping) return;
        if (this.searchApplied) this.clearSearch();
        return;
      }

      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";

.tl-name__title,
.tl-card__title {
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.tl-name__logo,
.tl-card__logo {
  font-weight: 700;
  font-size: 12px;
}

.tl-nicks {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.tl-nick {
  padding: 2px 8px;
  border-radius: 7px;
  background: var(--tl-soft);
  color: var(--tl-text);
  font-size: 11.5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
</style>
